import { FC } from 'react';
import { SubscriptionHookOptions, useSubscription } from '@apollo/client';
import { Center } from '@chakra-ui/react';
import { pathEq } from 'ramda';

import useDebounce from 'common/hooks/useDebounce';
import ErrorCard from 'components/ErrorCard';
import { logEvent } from 'features/Analytics/analytics';

import BankEmptyState from '../BankEmptyState';
import { useBankOverviewSearchParams } from '../hooks/useBankOverviewSearchParams';
import useIsNewAccountCheckingBank from '../hooks/useIsNewAccountCheckingBank';
import { useTransactionsFilters } from '../hooks/useTransactionsFilters';
import Transactions from './component';
import { OnUpdatedTransactionDocument } from './graphql/subscriptions/onUpdatedTransaction.gql';
import useTransactionsSearch from './hooks/useTransactionsSearch';
import { TransactionsSearchDocument } from './hooks/useTransactionsSearch/transactionsSearch.gql';
import {
  addTransactionToCache,
  getFiltersLength,
  mergeTransactionConnectionResults,
  OnUpdatedTransactionSubscriptionType,
} from './utils';

interface UpdateTransactionSubscriptionData {
  updatedTransaction: OnUpdatedTransactionSubscriptionType;
}

const handleTransactionUpdated: SubscriptionHookOptions<UpdateTransactionSubscriptionData>['onData'] =
  ({ client, data: { data } }) => {
    if (data === undefined) {
      return;
    }

    addTransactionToCache({
      query: TransactionsSearchDocument,
      store: client,
      transaction: data.updatedTransaction,
    });
  };

const TransactionsContainer: FC = () => {
  useSubscription(OnUpdatedTransactionDocument, {
    onData: handleTransactionUpdated,
  });
  const { bankAccountId } = useBankOverviewSearchParams();
  const { filters } = useTransactionsFilters();
  const query = useDebounce<string>(filters.q ?? '');

  const { called, data, error, fetchMore, loading, networkStatus, variables } =
    useTransactionsSearch({ bankAccountId, searchTerm: query });

  const { isNewAccountCheckingBank } = useIsNewAccountCheckingBank({
    companyUserId: filters.companyUserId,
    transactions: data,
  });

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  const loadMore = (): void => {
    logEvent({
      name: 'Transactions More Loaded',
      usePrefix: false,
    });

    fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const isEqual = pathEq(
          previousResult,
          ['viewer', 'company', 'transactionsSearch', 'pageInfo'],
          fetchMoreResult,
        );
        if (isEqual) {
          return previousResult;
        }
        return mergeTransactionConnectionResults(
          previousResult,
          fetchMoreResult,
        );
      },
      variables: {
        after: data?.viewer.company.transactionsSearch.pageInfo.nextCursor,
      },
    });
  };

  // Because filters can be debounced, we rather rely on last query variables to
  // determine if it was or wasn't filtered
  const filtered = variables?.filters
    ? getFiltersLength(variables.filters, [
        'bankAccountId',
        'companyProfileId',
      ]) > 0
    : false;

  return (
    <>
      {isNewAccountCheckingBank && !filtered ? (
        <BankEmptyState />
      ) : (
        <Transactions
          filtered={filtered}
          loading={loading || !called}
          loadMore={loadMore}
          networkStatus={networkStatus}
          transactions={data?.viewer?.company?.transactionsSearch}
        />
      )}
    </>
  );
};

export default TransactionsContainer;
